import { useContext } from "react";
import Image from "next/image";
import {
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Link,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Box,
  Flex,
  useColorMode,
  Text,
  Center,
  useColorModeValue,
} from "@chakra-ui/react";

import { AiOutlineMore } from "react-icons/ai";

import ThemeSwitcher from "../ThemeSwitcher";

import chevron_right_light from "assets/svg/chevron_right_light.svg";
import chevron_right_dark from "assets/svg/chevron_right_dark.svg";
import faq_dark from "assets/svg/faq_dark.svg";
import faq_light from "assets/svg/faq_light.svg";
import support_light from "assets/svg/support_light.svg";
import support_dark from "assets/svg/support_dark.svg";
import theme_dark from "assets/svg/theme_dark.svg";
import theme_light from "assets/svg/theme_light.svg";

// Types
import { IIndexable } from "types/common";

// Contexts
import { StakeContext } from "providers/StakeProvider";

interface HeaderProps {
  headerOptions: any;
  shouldDisplayMenu: boolean;
  onChangeDisplayMenu(isVisible: boolean): void;
}

const SUBHEADER_ICONS: IIndexable = {
  light: {
    support: support_light,
    faq: faq_light,
    theme: theme_light,
  },
  dark: {
    support: support_dark,
    faq: faq_dark,
    theme: theme_dark,
  },
};

const SubHeader = ({
  headerOptions,
  shouldDisplayMenu,
  onChangeDisplayMenu,
}: HeaderProps) => {
  const { track } = useContext(StakeContext);
  const { colorMode } = useColorMode();

  const ChevronRight = useColorModeValue(
    chevron_right_light,
    chevron_right_dark
  );

  const getHeaderItem = (item: any) => {
    return (
      <Flex justifyContent="space-between" alignItems="center" mt="1.5rem">
        <Flex
          alignItems="center"
          onClick={() => track(item.title.toString().toUpperCase())}
        >
          <Center
            h={{ base: "32px", md: "40px" }}
            w={{ base: "32px", md: "40px" }}
            borderRadius="50%"
            bg="#F5F5F7"
            _dark={{
              bg: "#202022",
            }}
          >
            {item.key && (
              <Image
                width="24px"
                height="24px"
                src={SUBHEADER_ICONS[colorMode][item.key]}
                alt={item.key}
              />
            )}
          </Center>

          <Text fontWeight="600" fontSize="16px" lineHeight="24px" ml="1rem">
            {item.title}
          </Text>
        </Flex>

        {item.key === "theme" ? (
          <Box>
            <ThemeSwitcher />
          </Box>
        ) : (
          <Image
            src={ChevronRight}
            width="24px"
            height="24px"
            alt="link-icon"
          />
        )}
      </Flex>
    );
  };

  const getSubHeader = () =>
    headerOptions.map((item: { event: string; link: string | undefined }) =>
      item?.link ? (
        <Link
          key={item.link}
          cursor="pointer"
          href={item.link}
          target="_blank"
          rel="nofollow"
        >
          {getHeaderItem(item)}
        </Link>
      ) : (
        <Box key={`subHeader-${item.event}`}>{getHeaderItem(item)}</Box>
      )
    );

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label="Options"
          icon={
            <AiOutlineMore height="24px" width="24px" color="brand.textColor" />
          }
          variant="icon"
          bg="brand.backgroundLight"
          display={{ base: "none", xl: "inline-flex" }}
          borderRadius="0.5rem"
          h={{ base: "32px", sm: "52px" }}
          w={{ base: "32px", sm: "52px" }}
          ml="1.5rem"
          border="none"
          outline="none"
          _focus={{
            border: "none",
            outline: "none",
          }}
          _hover={{
            bg: "brand.primary",
            color: "brand.textLight",
          }}
        />
        <MenuList
          borderRadius="1rem"
          bg="brand.backgroundLight"
          p="0rem 1.5rem 2rem"
          display="flex"
          flexDirection="column"
          boxShadow="4px 24px 40px rgba(0, 0, 0, 0.12)"
          width="280px"
          maxW="98%"
          zIndex="4"
        >
          {getSubHeader()}
        </MenuList>
      </Menu>
      <Drawer
        isOpen={shouldDisplayMenu}
        onClose={() => onChangeDisplayMenu(false)}
        placement="bottom"
      >
        <DrawerOverlay />
        <DrawerContent
          bg="brand.backgroundLight"
          borderTopRadius="2rem"
          p="2.5rem 1rem"
        >
          <DrawerBody p="0">
            {headerOptions.map(
              (item: { event: string; link: string | undefined }) =>
                item?.link ? (
                  <Link
                    key={item.link}
                    cursor="pointer"
                    href={item.link}
                    target="_blank"
                    rel="nofollow"
                  >
                    {getHeaderItem(item)}
                  </Link>
                ) : (
                  <Box key={`header-${item.event}`}>{getHeaderItem(item)}</Box>
                )
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default SubHeader;
